import { localMonth } from "@/pages/ComplianceOverview";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { visibleSections } from "@/lib/navSections";
import { isManager, hasModule } from "@/lib/access";
import { DepartmentReport } from "@/components/DepartmentReport";
import { ArrowUpRight } from "lucide-react";

const Stat = ({ value, label, tone, testid }) => (
  <span data-testid={testid} className={`text-[11px] px-2 py-0.5 rounded-sm border mono ${tone}`}>
    <b>{value ?? "–"}</b> {label}
  </span>
);

const TONES = { red: "bg-rose-50 text-rose-700 border-rose-200", amber: "bg-amber-50 text-amber-700 border-amber-200", slate: "bg-white text-blue-800 border-blue-200" };

const useCounts = (obScope, obOn, tkOn, inqOn, ccOn, vOn) => {
  const [c, setC] = useState({});
  useEffect(() => {
    if (!obScope) return;
    if (obOn) api.get(`/dashboard/stats?scope=${obScope}`).then((r) => setC((x) => ({ ...x, ob: r.data }))).catch(() => {});
    if (tkOn) api.get("/tasks/dashboard/stats").then((r) => setC((x) => ({ ...x, tk: r.data }))).catch(() => {});
    if (ccOn) api.get(`/compliance/overview?month=${localMonth()}`).then((r) => setC((x) => ({ ...x, cc: r.data.totals }))).catch(() => {});
    if (vOn) api.get("/vault/summary").then((r) => setC((x) => ({ ...x, vault: r.data }))).catch(() => {});
    if (inqOn) api.get("/inquiries/followups/summary").then((r) => setC((x) => ({ ...x, inq: r.data }))).catch(() => {});
  }, [obScope, obOn, tkOn, inqOn, ccOn, vOn]);
  return c;
};

const statsFor = (id, c) => {
  if (id === "obligations") return [
    { v: c.ob?.overdue_count, l: "overdue", t: "red" }, { v: c.ob?.upcoming_count, l: "due in 7 days", t: "amber" }, { v: c.ob?.total_obligations, l: "active", t: "slate" }];
  if (id === "vault") return [
    { v: c.vault?.overdue, l: "overdue returns", t: "red" }, { v: c.vault?.issued, l: "issued out", t: "amber" }, { v: c.vault?.total, l: "files", t: "slate" }];
  if (id === "compliance") return [
    { v: c.cc?.backlog, l: "overdue", t: "red" }, { v: c.cc?.pending, l: "pending this month", t: "amber" }, { v: c.cc?.done, l: "filed this month", t: "slate" }];
  if (id === "tasks") return [
    { v: c.tk?.overdue, l: "overdue", t: "red" }, { v: c.tk?.due_today, l: "due today", t: "amber" }, { v: c.tk?.total, l: "open tasks", t: "slate" }];
  if (id === "inquiries") return [
    { v: c.inq?.overdue, l: "follow-ups overdue", t: "red" }, { v: c.inq?.today, l: "due today", t: "amber" }, { v: c.inq?.open, l: "open", t: "slate" }];
  return [];
};

const Tile = ({ s, stats, idx, onOpen }) => {
  const Icon = s.icon;
  return (
    <button type="button" data-testid={`home-tile-${s.id}`} onClick={onOpen} style={{ animationDelay: `${idx * 60}ms` }}
      className="home-tile group text-left bg-[#EAF2FF] border border-blue-200/70 rounded-lg p-6 flex flex-col gap-4 min-h-[190px] hover:bg-[#E1ECFF] hover:border-blue-400 hover:-translate-y-0.5 transition-[transform,border-color,box-shadow,background-color] hover:shadow-[0_10px_24px_-12px_rgba(29,78,216,0.35)]">
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-white border border-blue-200 text-blue-700 shadow-sm"><Icon className="w-5 h-5" strokeWidth={1.75} /></div>
        <ArrowUpRight className="w-5 h-5 text-blue-300 group-hover:text-blue-700 transition-colors" />
      </div>
      <div>
        <div className="text-lg font-semibold tracking-tight text-slate-900">{s.label}</div>
        <div className="text-sm text-slate-500 mt-1">{s.desc}</div>
      </div>
      <div className="mt-auto flex flex-wrap gap-1.5">
        {stats.length ? stats.map((x) => <Stat key={x.l} testid={`home-stat-${s.id}-${x.l.replace(/\s+/g, "-")}`} value={x.v} label={x.l} tone={TONES[x.t]} />)
          : <span className="text-[11px] text-blue-700/70">{s.links.length} {s.links.length === 1 ? "page" : "pages"}</span>}
      </div>
    </button>
  );
};

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const obScope = user ? (isManager(user) ? "team" : "mine") : null;
  const counts = useCounts(obScope, hasModule(user, "obligations"), hasModule(user, "tasks"), hasModule(user, "inquiries"), hasModule(user, "compliance"), hasModule(user, "vault") || !!user?.vault_shared);
  const sections = visibleSections(user);
  return (
    <div data-testid="home-launcher" className="pt-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">Welcome back</div>
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-1" data-testid="home-greeting">{user?.name?.split(" ")[0] || "Hello"}<span className="text-blue-700">.</span></h1>
      <p className="text-sm md:text-base text-slate-500 mt-2">Choose a section to get started.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-10">
        {sections.map((s, i) => <Tile key={s.id} s={s} idx={i} stats={statsFor(s.id, counts)} onOpen={() => navigate(s.links[0].to)} />)}
      </div>
      {isManager(user) && <DepartmentReport user={user} />}
    </div>
  );
}
